import { AdbDaemonWebUsbDevice } from '@yume-chan/adb-daemon-webusb';
import { Adb } from '@/api/adb/adb';
import { filesUrl } from '@/utility/config';
import { Files, Package as Application } from '@/entities/configuration';
import { PARAMETERS_FILE } from '@/utility/devices';

export enum LicenseType {
    NeoDTx = 'com.neodtx.bliss.home',
    Butterfly = 'com.butterflytx.bliss.home',
    Enozone = 'com.Enozone.bliss.vr.home'
}

export enum DeviceType {
    Phone = 'phone',
    PicoG2 = 'PICOA7910',
    PicoG3 = 'PICOA7Q10'
}

// export const bootAnimationPath = '/storage/emulated/0/bootanimation/';

export class Wifi {
    enabled = false;
    connected = false;

    static parse(data: string): Wifi {
        const w = new Wifi();
        w.enabled = (data.match(/Wi-Fi is (\w+)/)![1]! ?? 'disabled') == 'enabled';
        w.connected =
            (data.match(/mNetworkInfo.*state: (\w+)/)![1]! ?? 'DISCONNECTED') == 'CONNECTED';
        return w;
    }
}

export class Battery {
    acPowered = false;
    usbPowered = false;
    wirelessPowered = false;
    chargeCounter = 0;
    status = 0;
    health = 0;
    present = false;
    level = 0;
    scale = 0;
    voltage = 0;
    temperature = 0;
    technology = '';

    get percent(): number {
        return (this.level * 100) / this.scale;
    }

    static parse(data: string): Battery {
        const b = new Battery();
        if (!!data && !data.startsWith("Can't find service")) {
            b.acPowered = (data.match(/AC powered: (true|false)/)![1]! ?? 'false') == 'true';
            b.usbPowered = (data.match(/USB powered: (true|false)/)![1]! ?? 'false') == 'true';
            b.wirelessPowered =
                (data.match(/Wireless powered: (true|false)/)![1]! ?? 'false') == 'true';
            b.chargeCounter = Number(data.match(/Charge counter: -?(\d+)/)![1]!) ?? 0;
            b.status = Number(data.match(/status: (\d+)/)![1]!) ?? 0;
            b.health = Number(data.match(/health: (\d+)/)![1]!) ?? 0;
            b.present = (data.match(/present: (true|false)/)![1]! ?? 'false') == 'true';
            b.level = Number(data.match(/level: (\d+)/)![1]!) ?? 0;
            b.scale = Number(data.match(/scale: (\d+)/)![1]!) ?? 0;
            b.voltage = Number(data.match(/voltage: (\d+)/)![1]!) / 10;
            b.temperature = Number(data.match(/temperature: (\d+)/)![1]!) / 10;
            b.technology = data.match(/technology: ([\w-]+)/)![1]! ?? '';
        }
        return b;
    }
}

interface Apps {
    [key: string]: any;
}
export type License = {
    owner: string;
    usage: string;
    issued?: Date;
    expires?: Date;
    apps: Apps;
};

export class Device {
    name: string;
    serial: string;
    deviceName = '';
    deviceAdmin = '';
    modelName = '';
    manufacturer = '';
    systemVersion = '';
    wifi = new Wifi();
    battery = new Battery();
    currentDate: Date = new Date(1970, 1, 1);
    source: AdbDaemonWebUsbDevice;
    packages: Package[] = [];

    constructor(name: string, serial: string, source: AdbDaemonWebUsbDevice) {
        this.name = name;
        this.serial = serial;
        this.source = source;
    }

    is(type: DeviceType): boolean {
        if (this.manufacturer.toUpperCase() == 'PICO') {
            return type.toString() == this.deviceName;
        }
        return type == DeviceType.Phone;
    }

    isInstalled(pkg: string, versionCode: number) {
        const found = this.packages.find((d) => d.name === pkg);
        if (!found) return false;
        if (versionCode == -1) return true;
        return found.versionCode == versionCode;
    }

    getVersionCode(apks?: string[]): number {
        if (!apks?.length) return -1;
        const apkName = apks[0];
        const versionName = apkName.replace('.apk', '').split('_').pop() || '';
        const numbers = versionName.match(/\d+/g);
        if (!numbers || numbers.length === 0) return -1;
        return parseInt(numbers[3]);
    }

    async loadInfo(): Promise<void> {
        const adb = new Adb(this.serial);
        try {
            await adb.connect();
            await this.loadDeviceInfo(adb);
        } finally {
            await adb.close();
        }
    }

    async reboot(opt = '') {
        const adb = new Adb(this.serial);
        try {
            await adb.connect();
            if (opt) {
                // Pour les options spéciales (bootloader, recovery, etc.)
                await adb.shell(`reboot ${opt}`);
                await adb.close();
            } else {
                // Reboot normal avec gestion des erreurs USB
                await adb.reboot();
            }
        } catch (e) {
            console.error(e);
        }
    }

    public async loadPackagesInfo(packages: string[]): Promise<void> {
        const adb = new Adb(this.serial);
        try {
            await adb.connect();
            const pp = this.packages.filter((p) => packages.indexOf(p.name) > -1);
            for (const p of pp) {
                const dump = await adb.shell(`dumpsys package ${p.name}`);
                p.setup(dump);
            }
        } catch (e) {
            console.error(e);
        } finally {
            await adb.close();
        }
    }

    public async getProps(adb: Adb, props: string): Promise<string | number> {
        const res = await adb.shell(`getprop ${props}`);
        if (!isNaN(Number(res))) return Number(res);
        return res;
    }

    public async setProps(adb: Adb, props: string, value: string | number) {
        await adb.shell(`setprop ${props} ${value}`);
    }

    public async getFiles(adb: Adb, path: string) {
        const res = await adb.shell(`ls ${path}`);
        if (res.endsWith('No such file or directory')) return [];
        return res
            .split(/\s+/g)
            .filter((f) => f != '')
            .sort((a, b) => (a < b ? -1 : 1));
    }

    async configureLauncher(adb: Adb, launcher: string, enabled: boolean) {
        if (enabled) {
            await adb.shell(`cmd package set-home-activity ${launcher}`);
        } else {
            await adb.shell(`am task lock stop ${launcher}`);
            const re = /Launcher: ComponentInfo\{([\w./]+)}/g;
            const res = await adb.shell(`adb shell cmd shortcut get-default-launcher`);
            const app = (re.exec(res) ?? ['', ''])[1];
            await adb.shell(`cmd package set-home-activity ${app}`);
        }
    }

    async configureDeviceAdmin(adb: Adb, rcv: string, active: boolean) {
        if (active) {
            await adb.shell(`dpm set-device-owner ${rcv}`);
            await adb.shell(`dpm set-active-admin ${rcv}`);
            this.deviceAdmin = rcv;
        } else {
            await adb.shell(`dpm remove-active-admin ${rcv}`);
            this.deviceAdmin = '';
        }
    }

    // async startApp(adb: Adb, pkg: string) {
    //     const an = await adb.shell(`dumpsys package | grep -i ${pkg} | grep Activity | head -n 1`);
    //     const activity = an.replace(/\w+ ([\w\\./]+)/, '$1');
    //     await adb.shell(`am force-stop ${pkg}`);
    //     const res = await adb.shell(`am start -n ${activity}`);
    //     if (res.indexOf(`Error: Activity class {${activity}} does not exist`) > -1)
    //         throw 'Failure [ACTIVITY_DOES_NOT_EXIST]';
    // }

    public async installBundle(
        adb: Adb,
        baseUrl: string,
        pkg: string,
        apks: string[],
        isTestOnly = false
    ) {
        const path = '/data/local/tmp/app';
        try {
            await adb.shell(`mkdir -p ${path}`);
            for (const apk of apks) {
                const url = `${baseUrl}/${apk}`;
                await adb.stream(url, path);
            }
            const res = await adb.shell(`ls -l ${path}`);
            const toInstall = res
                .split('\n')
                .filter((l) => l.indexOf('total'))
                .map((l) => {
                    const arr = l.split(new RegExp('\\s+', 'gi'));
                    return { size: Number(arr[4]), name: `${path}/${arr[7]}` };
                });
            if (toInstall.length != apks.length)
                throw `Missing file(s) to install (${toInstall.length} file found)`;
            const totalSize = toInstall.reduce((n, { size }) => n + size, 0);
            let cmd = `pm install-create -r -S ${totalSize}`;
            if (isTestOnly) cmd = `pm install-create -t -r -S ${totalSize}`;
            const sess = await adb.shell(cmd);
            const sessId = sess.match(/\d+/g)!.pop();
            if (sessId) {
                for (let i = 0; i < toInstall.length; i++) {
                    const install = toInstall[i];
                    await adb.shell(
                        `pm install-write -S ${install.size} ${sessId} ${i} ${install.name}`
                    );
                }
                const res = await adb.shell(`pm install-commit ${sessId}`);
                if (!res.startsWith('Success')) throw 'install failed';
            }
        } finally {
            // Nettoyage best-effort. Sur une connexion morte ce `rm` leve a son tour,
            // et son erreur remplacait celle d'origine : on perdait la seule qui
            // disait pourquoi l'installation avait echoue.
            try {
                await adb.shell(`rm -r ${path}`);
            } catch (e) {
                console.error(e);
            }
        }
    }

    public async uninstall(adb: Adb, pkg: string) {
        await adb.shell(`pm uninstall ${pkg}`);
    }

    public async setPermission(adb: Adb, pkg: string, permission: string) {
        await adb.shell(`pm grant ${pkg} ${permission}`);
    }

    // private async loadTestLog(type: string): Promise<string> {
    //     const resp = await fetch(`http://localhost:8079/tests/${type}.log`);
    //     return await resp.text();
    // }

    protected async loadDeviceInfo(adb: Adb): Promise<void> {
        this.deviceName = await adb.shell('getprop ro.product.device');
        this.modelName = await adb.shell('getprop ro.product.model');
        this.manufacturer = await adb.shell('getprop ro.product.manufacturer');
        this.systemVersion = await adb.shell('getprop ro.build.display.id');
        const wifi = await adb.shell('dumpsys wifi');
        if (wifi.startsWith("Can't find service")) throw 'Not ready';
        this.wifi = Wifi.parse(wifi);
        const battery = await adb.shell('dumpsys battery');
        if (battery.startsWith("Can't find service")) throw 'Not ready';
        this.battery = Battery.parse(battery);
        const re = /\s+admin=ComponentInfo\{([\w./]+)}/g;
        const policy = await adb.shell('dumpsys device_policy');
        this.deviceAdmin = this.parseReceiver((re.exec(policy) ?? ['', ''])[1].trim());
        const date = await adb.shell("date '+%Y-%m-%d %H:%M:%S'");
        this.currentDate = new Date(Date.parse(date));
        const list = await adb.shell(`pm list packages --show-versioncode`);
        this.packages = list.split('\n').map((l) => new Package(l));
        if (this.packages.length == 0) throw 'Not ready';
    }

    private parseReceiver(receiver: string): string {
        if (!receiver) return receiver;
        const arr = receiver.split('/');
        const pkg = arr.shift()!;
        const rcv = arr.shift()!.split('.').pop()!;
        if (receiver == `${pkg}/${pkg}.${rcv}`) return `${pkg}/.${rcv}`;
        return receiver;
    }
}

export abstract class VRDevice extends Device {
    protected readonly configNamePath = '/storage/emulated/0/setup.cfg';
    private _licenseUrl = '';
    private _licenseFileName = '';
    public currentConfig = '';
    public serverLicense = '';
    public deviceLicense = '';
    /** Contenu du `parameters.pem` de reference (config), `null` si la config n'en fournit pas. */
    public serverParameters: string | null = null;
    /** Contenu du `parameters.pem` present sur le casque, vide s'il est absent. */
    public deviceParameters = '';

    get hasServerLicense(): boolean {
        return !!this.serverLicense;
    }

    get hasDeviceLicense(): boolean {
        return !!this.deviceLicense;
    }
    public resetDeviceLicense(): void {
        this.deviceLicense = '';
    }

    get hasLicenseToUpdate(): boolean {
        return this.deviceLicense != this.serverLicense;
    }

    get hasServerParameters(): boolean {
        return this.serverParameters != null;
    }

    /**
     * Le serveur fait foi : des que la config fournit un `parameters.pem`, celui du
     * casque doit lui etre strictement identique. La comparaison se fait sur le contenu
     * car un nouveau PEM a tres souvent la meme taille que l'ancien.
     */
    get hasParametersToUpdate(): boolean {
        return this.serverParameters != null && this.serverParameters != this.deviceParameters;
    }

    /**
     * Source serveur du `parameters.pem` pour CE casque : l'entree `files` de la config
     * qui le fournit, deja arbitree entre config par casque et config generique (voir
     * `findParametersFiles`). `null` si la config n'en fournit pas.
     */
    public parametersSource: Files | null = null;

    /** URL du `parameters.pem` de reference, vide sans source. */
    get parametersUrl(): string {
        if (this.parametersSource == null) return '';
        return `${this.parametersSource.baseUrl}/${PARAMETERS_FILE}`;
    }

    /** Chemin du `parameters.pem` sur le casque, vide sans source. */
    get parametersPath(): string {
        if (this.parametersSource == null) return '';
        return `${this.parametersSource.path}/${PARAMETERS_FILE}`;
    }

    /** Lu dans la meme connexion ADB que la licence, au chargement du casque. */
    async loadDeviceParameters(adb: Adb): Promise<void> {
        this.deviceParameters = '';
        if (this.parametersSource == null) return;
        try {
            this.deviceParameters = await adb.pull(this.parametersPath);
        } catch (e) {
            if (`${e}` != 'No such file or directory') console.error(e);
        }
    }

    async loadServerParameters(): Promise<void> {
        this.serverParameters = null;
        if (this.parametersSource == null) return;
        try {
            const resp = await fetch(`${this.parametersUrl}?v=${Date.now()}`);
            if (resp.status == 200) this.serverParameters = await resp.text();
        } catch (e) {
            console.error(e);
        }
    }

    get hasTimeUpToDate() {
        return this.currentDate.toLocaleDateString() == new Date().toLocaleDateString();
    }

    get licenseUrl(): string {
        return this._licenseUrl;
    }

    get licenseFileName(): string {
        return this._licenseFileName;
    }

    async loadServerLicense(): Promise<void> {
        for (const ext of ['sign', 'pem']) {
            const url = filesUrl + `/licenses/license_${this.serial}.${ext}?v=${Date.now()}`;
            try {
                const resp = await fetch(url);
                if (resp.status == 200) {
                    this._licenseUrl = url;
                    this._licenseFileName = `license.${ext}`;
                    this.serverLicense = await resp.text();
                }
            } catch (e) {
                console.error(e);
            }
        }
    }

    public abstract parseLicense(license: string): License;

    authorizedInstall(app: Pick<Application, 'pkg'>): boolean {
        let pkg = app.pkg;
        if (pkg.startsWith('com.neodtx.endocare')) pkg = 'com.neodtx.endocare';
        if (pkg.startsWith('com.butterflytx.endocare')) pkg = 'com.butterflytx.endocare';
        if (this.hasServerLicense) {
            const licence = this.parseLicense(this.serverLicense);
            return !!licence.apps[pkg];
        }
        if (this.hasDeviceLicense) {
            const licence = this.parseLicense(this.deviceLicense);
            return !!licence.apps[pkg];
        }
        return false;
    }

    async writeConfigName(adb: Adb, content: string): Promise<void> {
        try {
            return this.writeToFile(adb, this.configNamePath, content);
        } catch (e) {
            console.error(e);
        }
    }

    async readConfigName(adb: Adb): Promise<void> {
        try {
            this.currentConfig = await adb.pull(this.configNamePath);
        } catch (e) {
            if (`${e}` != 'No such file or directory') console.error(e);
        }
    }

    protected async writeToFile(adb: Adb, path: string, content: string): Promise<void> {
        const fileName = path.split('/').pop()!;
        const blob = new Blob([content], { type: 'text/plain' });
        const file = new File([blob], fileName, { type: 'text/plain' });
        await adb.push(file, path);
    }

    protected async startAndWaitClose(
        adb: Adb,
        pkg: string,
        activity: string,
        args: { [k: string]: any } = {}
    ): Promise<void> {
        await adb.ensureScreenOn();
        await adb.shell(`am force-stop ${pkg}`);
        const parts = [`am start -W -S -n ${pkg}/.${activity}`];
        for (const arg in args) {
            parts.push(`--es ${arg} ${args[arg]}`);
        }
        await adb.shell(parts.join(' '));
        let running = true;
        do {
            // Sans pause, ce sondage envoyait une quarantaine de commandes ADB par
            // seconde : ca martelait le lien USB et noyait les logs (pres de 200
            // lignes `pidof` pour quelques secondes d'attente).
            await adb.wait(250);
            const res = await adb.shell(`pidof ${pkg}`);
            running = res != '';
        } while (running);
        await adb.wait(100);
    }

    async removeUselessFiles(adb: Adb) {
        const types = [LicenseType.NeoDTx, LicenseType.Butterfly, LicenseType.Enozone];
        for (const licenseType of types) {
            for (const ext of ['sign', 'pem']) {
                const path = `/sdcard/Android/data/${licenseType}/files/license.${ext}`;
                await adb.shell(`rm -f ${path}`);
            }
        }
        for (const file of ['btx.part.id', 'btx.device.customname']) {
            const path = `/storage/emulated/0/${file}`;
            await adb.shell(`rm -f ${path}`);
        }
    }
}

export class PicoG2Device extends VRDevice {
    /**
     * `parametersSource` doit etre renseigne avant l'appel : la config est lue en
     * premier, le casque ensuite, pour comparer licence et parametres en une seule
     * connexion ADB.
     */
    override async loadInfo(): Promise<void> {
        const adb = new Adb(this.serial);
        try {
            await adb.connect();
            await this.loadDeviceInfo(adb);
            await this.loadDeviceLicense(adb);
            await this.loadDeviceParameters(adb);
            await this.readConfigName(adb);
        } finally {
            await adb.close();
        }
        await this.loadServerLicense();
        await this.loadServerParameters();
    }

    async loadDeviceLicense(adb: Adb): Promise<void> {
        const path = `/sdcard/Android/data/${LicenseType.Enozone}/files/license.sign`;
        try {
            this.deviceLicense = await adb.pull(path);
        } catch (e) {
            this.deviceLicense = '';
        }
    }

    async pushDeviceLicense(adb: Adb): Promise<boolean> {
        const path = `/sdcard/Android/data/${LicenseType.Enozone}/files`;
        await adb.shell(`rm -rf ${path}/${this.licenseFileName}`);
        const res = await adb.stream(this.licenseUrl, path, this.licenseFileName);
        await adb.wait(10);
        return res.indexOf('Success') > -1;
    }

    async setDatetime(adb: Adb): Promise<void> {
        const date = new Date();
        const datetime =
            date.toLocaleDateString('fr').replaceAll('/', '-') +
            ' ' +
            date.toLocaleTimeString('fr');
        await adb.shell(
            `am start-foreground-service -n com.blissdtx.maintenanceservice/.MaintenanceService --es date "${datetime}"`
        );
    }

    public override async getFiles(adb: Adb, path: string) {
        const res = await super.getFiles(adb, path);
        if (path == '/storage/emulated/0/Video360/WildImmersion') {
            res.unshift('[INTERNAL_ONLY]_Drop_SpecificWildVideoFile.bat');
        }
        return res;
    }

    get license(): License | null {
        if (!this.hasDeviceLicense) return null;
        return this.parseLicense(this.deviceLicense);
    }

    public override parseLicense(license: string): License {
        const parser = new DOMParser();
        const doc = parser.parseFromString(license, 'application/xml');
        const issuedString = this.getLicenseValue(doc, 'issued');
        const issued = new Date(Date.parse(issuedString!.split('/').reverse().join('-')));
        const appsString = this.getLicenseValue(doc, 'apps') || '';
        const apps = appsString.split('|').filter((x: string) => x !== '');
        const lic: License = {
            owner: this.getLicenseValue(doc, 'owner')!,
            usage: this.getLicenseValue(doc, 'usage')!,
            issued: issued,
            apps: this.mapToApp(apps)
        };
        const expiresString = this.getLicenseValue(doc, 'expires');
        if (expiresString)
            lic.expires = new Date(Date.parse(expiresString!.split('/').reverse().join('-')));
        return lic;
    }

    private getLicenseValue(doc: Document, key: string): string | null {
        const keys = doc.querySelectorAll('details idList string');
        const values = doc.querySelectorAll('details valuesList string');
        for (let i = 0; i < keys.length; i++) {
            if (keys[i].textContent === key) {
                return values[i]?.textContent || null;
            }
        }
        return null;
    }

    private mapToApp(apps: string[]): Apps {
        const res: Apps = {
            'com.butterflytx.bliss.kaitei': true,
            'com.Enozone.bliss.vr.kaitei': true,
            'com.butterflytx.bliss.mori': true,
            'com.Enozone.bliss.vr.mori': true,
            'com.butterflytx.bliss.nohara': true,
            'com.Enozone.bliss.vr.nohara': true,
            'com.butterflytx.bliss.uchuu': true,
            'com.Enozone.bliss.vr.uchuu': true,
            'com.butterflytx.maintenance': true,
            'com.blissdtx.maintenanceservice': true
        };
        if (apps.includes('BlissOne')) {
            res['com.butterflytx.bliss.home'] = true;
            res['com.Enozone.bliss.vr.home'] = true;
        }
        if (apps.includes('BlissO2')) {
            res['com.butterflytx.bliss.o2'] = true;
            res['com.butterflytx.bliss.o2.picog2'] = true;
        }
        if (apps.includes('WI')) {
            res['com.butterflytx.wildimmersion'] = true;
            res['com.effetpapillon.wildimmersion'] = true;
        }
        return res;
    }
}

export class PicoG3Device extends VRDevice {
    public setupLog = '';
    /** Secondes ecoulees depuis le dernier demarrage du casque, -1 si inconnu. */
    public uptime = -1;

    /**
     * `parametersSource` doit etre renseigne avant l'appel : la config est lue en
     * premier, le casque ensuite, pour comparer licence et parametres en une seule
     * connexion ADB.
     */
    override async loadInfo(): Promise<void> {
        const adb = new Adb(this.serial);
        try {
            await adb.connect();
            await this.loadDeviceInfo(adb);
            await this.loadDeviceLicense(adb);
            await this.loadDeviceParameters(adb);
            await this.readConfigName(adb);
            await this.readLogSetup(adb);
            await this.readUptime(adb);
        } finally {
            await adb.close();
        }
        await this.loadServerLicense();
        await this.loadServerParameters();
    }

    /**
     * Permet de savoir si le casque a redemarre depuis un instant donne : Bliss Home
     * reboote parfois de lui-meme a la fin de son propre setup, et il ne sert alors a
     * rien de proposer un redemarrage a l'utilisateur.
     */
    async readUptime(adb: Adb): Promise<void> {
        try {
            const res = await adb.shell('cat /proc/uptime');
            const seconds = Number.parseFloat(res.split(/\s+/)[0]);
            this.uptime = Number.isNaN(seconds) ? -1 : seconds;
        } catch (e) {
            this.uptime = -1;
        }
    }

    /** `true` si le casque a demarre apres l'instant donne (horloge du navigateur). */
    hasRebootedSince(timestamp: number): boolean {
        if (this.uptime < 0) return false;
        // 5 s de marge : le `loadInfo` qui lit l'uptime arrive toujours un peu
        // apres l'evenement USB qui l'a declenche.
        return this.uptime * 1000 < Date.now() - timestamp + 5000;
    }

    async loadDeviceLicense(adb: Adb): Promise<void> {
        const types = [LicenseType.NeoDTx, LicenseType.Butterfly];
        const extensions = ['pem', 'sign'];
        for (const licenseType of types) {
            for (const ext of extensions) {
                const path = `/sdcard/Android/data/${licenseType}/files/license.${ext}`;
                try {
                    this.deviceLicense = await adb.pull(path);
                    return;
                } catch (e) {
                    this.deviceLicense = '';
                }
            }
        }
    }

    async readLogSetup(adb: Adb) {
        try {
            this.setupLog = await adb.pull('/storage/emulated/0/headsetsetup.log');
        } catch (e) {
            this.setupLog = '';
        }
    }

    async pushDeviceLicense(adb: Adb, licenseType: LicenseType): Promise<boolean> {
        const path = `/sdcard/Android/data/${licenseType}/files`;
        await adb.shell(`rm -rf ${path}/${this.licenseFileName}`);
        const res = await adb.stream(this.licenseUrl, path, this.licenseFileName);
        await adb.wait(10);
        return res.indexOf('Success') > -1;
    }

    async setDatetime(adb: Adb, licType: LicenseType): Promise<void> {
        let pkg = 'com.butterflytx.maintenance';
        if (licType == LicenseType.NeoDTx) pkg = 'com.neodtx.maintenance';
        await adb.shell('settings put global auto_time 0');
        await this.startAndWaitClose(adb, pkg, 'MainActivity', { time_millis: Date.now() });
        await adb.wait(100);
        // const res = await adb.shell('logcat -d');
        // const lines = res.split('\n').filter((l) => l.indexOf('Maintenance: ') > -1);
        // lines.forEach((l) =>
        //     Log.debug({ action: 'logcat', cmd: 'set-time', result: l, serial: this.serial })
        // );
        const date = await adb.shell("date '+%Y-%m-%d %H:%M:%S'");
        this.currentDate = new Date(Date.parse(date));
    }

    async setupBlissOne(adb: Adb, licType: LicenseType): Promise<void> {
        let pkg = 'com.butterflytx.bliss.home';
        if (licType == LicenseType.NeoDTx) pkg = 'com.neodtx.bliss.home';
        await this.writeToFile(adb, '/storage/emulated/0/headsetsetup', 'setup');
        await this.startAndWaitClose(adb, pkg, 'HomePlayerActivity');
    }

    get license(): License | null {
        if (!this.hasDeviceLicense) return null;
        return this.parseLicense(this.deviceLicense);
    }

    public override parseLicense(license: string): License {
        const lic = this.from(license);
        let d = '';
        if (lic.apps['com.neodtx.bliss.home']) d = lic.apps['com.neodtx.bliss.home'].expires;
        else if (lic.apps['com.neodtx.bliss.one']) d = lic.apps['com.neodtx.bliss.one'].expires;
        else if (lic.apps['com.butterflytx.bliss.one'])
            d = lic.apps['com.butterflytx.bliss.one'].expires;
        else if (lic.apps['com.butterflytx.bliss.home'])
            d = lic.apps['com.butterflytx.bliss.home'].expires;
        const exp = Date.parse(d.split('/').reverse().join('-'));
        return {
            owner: lic.info.owner,
            usage: lic.globalSettings.usage,
            expires: new Date(exp),
            apps: this.mapApps(lic.apps)
        };
    }

    private mapApps(apps: any[]): Apps {
        const res: Apps = {
            'com.neodtx.bliss.kaitei': true,
            'com.neodtx.bliss.mori': true,
            'com.neodtx.bliss.mori.S': true,
            'com.neodtx.bliss.mori.L': true,
            'com.neodtx.bliss.mori.T': true,
            'com.neodtx.bliss.nohara': true,
            'com.neodtx.bliss.uchuu': true,
            'com.neodtx.maintenance': true,
            'com.butterflytx.bliss.kaitei': true,
            'com.butterflytx.bliss.mori': true,
            'com.butterflytx.bliss.nohara': true,
            'com.butterflytx.bliss.uchuu': true,
            'com.butterflytx.maintenance': true
        };
        for (const app in apps) res[app] = true;
        return res;
    }

    override authorizedInstall(app: Pick<Application, 'pkg'>): boolean {
        if (!app.pkg.endsWith('.home')) {
            return super.authorizedInstall(app);
        }
        const pkg = app.pkg.replace('.home', '.one');
        if (this.hasServerLicense) {
            const licence = this.parseLicense(this.serverLicense);
            return !!licence.apps[app.pkg] || !!licence.apps[pkg];
        }
        if (this.hasDeviceLicense) {
            const licence = this.parseLicense(this.deviceLicense);
            return !!licence.apps[app.pkg] || !!licence.apps[pkg];
        }
        return false;
    }

    from(lic: string) {
        if (this.isPEM(lic)) return this.fromPEM(lic);
        return JSON.parse(lic);
    }

    isPEM(lic: string) {
        return lic.startsWith('-----BEGIN');
    }

    fromPEM(pem: string) {
        const data = pem
            .replace(/-----BEGIN \w+-----/, '')
            .replace(/-----END \w+-----/, '')
            .split('\n')
            .join('')
            .trim();
        const json = JSON.parse(atob(data));
        return JSON.parse(atob(json.data));
    }
}

export class Package {
    readonly name: string;
    versionName = '';
    versionCode = 0;
    permissions: string[] = [];
    private dumpsys = '';

    constructor(raw: string) {
        if (raw.indexOf(' ') > -1) {
            const r = raw.split(' ');
            this.name = r[0].split(':')[1];
            this.versionCode = Number(r[1].split(':')[1]);
        } else {
            this.name = raw.split(':')[1];
        }
    }

    setup(dump: string): void {
        this.dumpsys = dump;
        this.versionName = this.getVersionName();
        this.permissions = this.getPermissions();
    }

    private getVersionName(): string {
        const m = this.dumpsys.match(/versionName=(.*)\n/);
        if (m && m.length > 1) return `v${m[1]}`;
        return 'v0.0.0';
    }

    private getPermissions(): string[] {
        const perms: string[] = [];
        if (!this.dumpsys) return perms;
        const ll = this.dumpsys.split('\n');
        for (const l of ll) {
            if (l.indexOf('.permission.') > -1) {
                if (l.indexOf(':') > -1) perms.push(l.split(':')[0].trim());
                else perms.push(l.trim());
            }
        }
        return perms;
    }
}
